import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AuthService } from 'src/app/auth.service';
import { FirebaseService } from 'src/app/services/firebase.service';
import {
  TimeEntry,
  TimesheetService,
} from 'src/app/services/timesheet.service';

declare var bootstrap: any;

interface OrgEmployee {
  id: string;
  displayName: string;
}

interface PeriodDayGroup {
  date: Date;
  dateKey: string;
  dayLabel: string;
  dateLabel: string;
  entries: TimeEntry[];
  dayTotal: number;
  isToday: boolean;
  gapNotes: GapNote[];
  rowSpan: number;
}

interface GapNote {
  afterIndex: number;
  minutes: number;
  isVoided: boolean;
  label: string;
}

@Component({
  selector: 'app-manager-timesheets',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './manager-timesheets.component.html',
  styleUrls: ['./manager-timesheets.component.scss'],
})
export class ManagerTimesheetsComponent implements OnInit {
  @ViewChild('manualEntryModal', { static: false })
  manualEntryModalRef?: ElementRef<HTMLDivElement>;

  @ViewChild('editEntryModal', { static: false })
  editEntryModalRef?: ElementRef<HTMLDivElement>;

  private readonly auth = inject(AuthService);
  private readonly firebaseService = inject(FirebaseService);
  private readonly timesheetService = inject(TimesheetService);
  private readonly fb = inject(FormBuilder);

  private readonly payPeriodAnchor = new Date(2024, 0, 7);

  employees: OrgEmployee[] = [];
  selectedEmployeeId = '';
  selectedEmployeeName = '';

  periodStart = new Date();
  periodEnd = new Date();
  periodLabel = '';

  groups: PeriodDayGroup[] = [];
  week1Subtotal = 0;
  week2Subtotal = 0;
  payPeriodTotal = 0;

  payRate: number | null = null;

  isLoadingEmployees = false;
  isLoadingEntries = false;
  manualSaving = false;
  editSaving = false;
  deleteSaving = false;

  errorMessage = '';

  selectedManualDateKey = '';
  selectedManualDateLabel = '';

  editEntryId = '';
  editEntryDateKey = '';
  showDeleteConfirm = false;

  readonly manualEntryForm = this.fb.nonNullable.group({
    timeIn: ['', Validators.required],
    timeOut: ['', Validators.required],
    note: [''],
  });

  readonly editEntryForm = this.fb.nonNullable.group({
    timeIn: ['', Validators.required],
    timeOut: ['', Validators.required],
    note: [''],
  });

  private manualModalInstance: any;
  private editModalInstance: any;

  async ngOnInit(): Promise<void> {
    this.initializeCurrentPeriod();
    await this.loadEmployees();
  }

  get estimatedPay(): number {
    if (this.payRate === null || Number.isNaN(this.payRate)) {
      return 0;
    }
    return this.payPeriodTotal * this.payRate;
  }

  get hasEmployeeSelected(): boolean {
    return !!this.selectedEmployeeId;
  }

  get periodDatesTopLabel(): string {
    if (!this.groups.length) {
      return '';
    }

    return this.groups
      .map((group) =>
        group.date.toLocaleDateString([], { month: 'short', day: 'numeric' }),
      )
      .join(' | ');
  }

  printTimesheet(): void {
    if (!this.hasEmployeeSelected) {
      this.errorMessage = 'Select an employee before printing timesheet.';
      return;
    }

    this.errorMessage = '';
    window.print();
  }

  async onEmployeeChange(): Promise<void> {
    const employee = this.employees.find(
      (e) => e.id === this.selectedEmployeeId,
    );
    this.selectedEmployeeName = employee?.displayName || '';
    this.errorMessage = '';

    if (!this.selectedEmployeeId) {
      this.groups = [];
      this.week1Subtotal = 0;
      this.week2Subtotal = 0;
      this.payPeriodTotal = 0;
      return;
    }

    await this.loadEntriesForCurrentPeriod();
  }

  async goToPreviousPeriod(): Promise<void> {
    this.periodStart = this.addDays(this.periodStart, -14);
    this.periodEnd = this.addDays(this.periodStart, 13);
    this.refreshPeriodLabel();
    if (this.selectedEmployeeId) {
      await this.loadEntriesForCurrentPeriod();
    } else {
      this.groups = this.buildPeriodGroups([]);
    }
  }

  async goToNextPeriod(): Promise<void> {
    this.periodStart = this.addDays(this.periodStart, 14);
    this.periodEnd = this.addDays(this.periodStart, 13);
    this.refreshPeriodLabel();
    if (this.selectedEmployeeId) {
      await this.loadEntriesForCurrentPeriod();
    } else {
      this.groups = this.buildPeriodGroups([]);
    }
  }

  onPayRateInput(value: string): void {
    const parsed = Number(value);
    this.payRate = Number.isNaN(parsed) ? null : parsed;
  }

  openManualEntryModal(group: PeriodDayGroup): void {
    if (!this.selectedEmployeeId) {
      this.errorMessage = 'Select an employee before adding a time entry.';
      return;
    }

    this.selectedManualDateKey = group.dateKey;
    this.selectedManualDateLabel = `${group.dayLabel}, ${group.dateLabel}`;
    this.manualEntryForm.reset({ timeIn: '', timeOut: '', note: '' });

    if (!this.manualModalInstance && this.manualEntryModalRef?.nativeElement) {
      this.manualModalInstance = new bootstrap.Modal(
        this.manualEntryModalRef.nativeElement,
      );
    }

    this.manualModalInstance?.show();
  }

  async submitManualEntry(): Promise<void> {
    if (
      !this.selectedEmployeeId ||
      this.manualSaving ||
      this.manualEntryForm.invalid
    ) {
      this.manualEntryForm.markAllAsTouched();
      return;
    }

    const { timeIn, timeOut, note } = this.manualEntryForm.getRawValue();
    const clockIn = this.buildDateTime(this.selectedManualDateKey, timeIn);
    const clockOut = this.buildDateTime(this.selectedManualDateKey, timeOut);

    if (!clockIn || !clockOut || clockOut <= clockIn) {
      this.errorMessage = 'Time Out must be after Time In.';
      return;
    }

    this.errorMessage = '';
    this.manualSaving = true;

    try {
      await this.timesheetService.addManualEntry(
        this.selectedEmployeeId,
        this.selectedEmployeeName,
        this.selectedManualDateKey,
        clockIn,
        clockOut,
        note || '',
      );

      this.manualModalInstance?.hide();
      await this.loadEntriesForCurrentPeriod();
    } catch (error: any) {
      this.errorMessage = error?.message || 'Failed to add manual entry.';
    } finally {
      this.manualSaving = false;
    }
  }

  openEditEntryModal(entry: TimeEntry): void {
    this.editEntryId = entry.id;
    this.editEntryDateKey = entry.date;
    this.showDeleteConfirm = false;

    const timeIn = this.toTimeInputValue(this.toDate(entry.clockIn));
    const timeOut = entry.clockOut
      ? this.toTimeInputValue(this.toDate(entry.clockOut))
      : '';

    this.editEntryForm.reset({
      timeIn,
      timeOut,
      note: entry.note || '',
    });

    if (!this.editModalInstance && this.editEntryModalRef?.nativeElement) {
      this.editModalInstance = new bootstrap.Modal(
        this.editEntryModalRef.nativeElement,
      );
    }

    this.editModalInstance?.show();
  }

  async saveEditedEntry(): Promise<void> {
    if (this.editSaving || this.editEntryForm.invalid || !this.editEntryId) {
      this.editEntryForm.markAllAsTouched();
      return;
    }

    const { timeIn, timeOut, note } = this.editEntryForm.getRawValue();
    const clockIn = this.buildDateTime(this.editEntryDateKey, timeIn);
    const clockOut = this.buildDateTime(this.editEntryDateKey, timeOut);

    if (!clockIn || !clockOut || clockOut <= clockIn) {
      this.errorMessage = 'Time Out must be after Time In.';
      return;
    }

    this.errorMessage = '';
    this.editSaving = true;

    try {
      await this.timesheetService.updateEntry(
        this.editEntryId,
        clockIn,
        clockOut,
        note || '',
      );

      this.editModalInstance?.hide();
      await this.loadEntriesForCurrentPeriod();
    } catch (error: any) {
      this.errorMessage = error?.message || 'Failed to update entry.';
    } finally {
      this.editSaving = false;
    }
  }

  async confirmDeleteEntry(): Promise<void> {
    if (!this.editEntryId || this.deleteSaving) {
      return;
    }

    this.deleteSaving = true;
    this.errorMessage = '';

    try {
      await this.timesheetService.deleteEntry(this.editEntryId);
      this.editModalInstance?.hide();
      await this.loadEntriesForCurrentPeriod();
    } catch (error: any) {
      this.errorMessage = error?.message || 'Failed to delete entry.';
    } finally {
      this.deleteSaving = false;
      this.showDeleteConfirm = false;
    }
  }

  formatTime(value: any): string {
    const date = this.toDate(value);
    return date.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  trackByDate(_index: number, group: PeriodDayGroup): string {
    return group.dateKey;
  }

  getGapNote(group: PeriodDayGroup, afterIndex: number): GapNote | null {
    return (
      group.gapNotes.find((note) => note.afterIndex === afterIndex) || null
    );
  }

  private async loadEmployees(): Promise<void> {
    const orgId = this.auth.getUsersOrgId();
    if (!orgId) {
      this.errorMessage = 'Organization not found. Please log in again.';
      return;
    }

    this.isLoadingEmployees = true;

    try {
      const employees = await this.firebaseService.getEmployeesByOrgId(orgId);
      this.employees = employees
        .filter((employee: any) => {
          const role = String(employee?.role || '').toLowerCase();
          return role === 'employee' || role === 'manager';
        })
        .map((employee: any) => ({
          id: employee.id,
          displayName: this.resolveEmployeeDisplayName(employee),
        }))
        .filter(
          (employee: OrgEmployee) => !!employee.id && !!employee.displayName,
        )
        .sort((a: OrgEmployee, b: OrgEmployee) =>
          a.displayName.localeCompare(b.displayName),
        );

      this.groups = this.buildPeriodGroups([]);
    } catch (error: any) {
      this.errorMessage = error?.message || 'Failed to load employees.';
    } finally {
      this.isLoadingEmployees = false;
    }
  }

  private async loadEntriesForCurrentPeriod(): Promise<void> {
    if (!this.selectedEmployeeId) {
      return;
    }

    this.isLoadingEntries = true;

    try {
      const entries = await this.timesheetService.getEntriesForPeriod(
        this.selectedEmployeeId,
        this.periodStart,
        this.periodEnd,
      );

      this.groups = this.buildPeriodGroups(entries);
      this.week1Subtotal = this.sumDayTotals(this.groups.slice(0, 7));
      this.week2Subtotal = this.sumDayTotals(this.groups.slice(7, 14));
      this.payPeriodTotal = this.week1Subtotal + this.week2Subtotal;
    } catch (error: any) {
      this.errorMessage = error?.message || 'Failed to load timesheet entries.';
    } finally {
      this.isLoadingEntries = false;
    }
  }

  private buildPeriodGroups(entries: TimeEntry[]): PeriodDayGroup[] {
    const byDate = new Map<string, TimeEntry[]>();
    const todayKey = this.toDateKey(new Date());

    for (const entry of entries) {
      if (!byDate.has(entry.date)) {
        byDate.set(entry.date, []);
      }
      byDate.get(entry.date)!.push(entry);
    }

    const groups: PeriodDayGroup[] = [];
    for (let i = 0; i < 14; i++) {
      const date = this.addDays(this.periodStart, i);
      const dateKey = this.toDateKey(date);
      const sortedDayEntries = (byDate.get(dateKey) || [])
        .slice()
        .sort(
          (a, b) =>
            this.toDate(a.clockIn).getTime() - this.toDate(b.clockIn).getTime(),
        );
      const gapNotes = this.buildGapNotes(sortedDayEntries);
      const dayTotal =
        this.timesheetService.calculateDayTotal(sortedDayEntries);

      groups.push({
        date,
        dateKey,
        dayLabel: date.toLocaleDateString([], { weekday: 'short' }),
        dateLabel: date.toLocaleDateString([], {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        }),
        entries: sortedDayEntries,
        dayTotal,
        isToday: dateKey === todayKey,
        gapNotes,
        rowSpan: sortedDayEntries.length + gapNotes.length,
      });
    }

    return groups;
  }

  private initializeCurrentPeriod(): void {
    const currentSunday = this.getSunday(new Date());
    const diffWeeks = Math.floor(
      this.diffInDays(this.payPeriodAnchor, currentSunday) / 7,
    );
    const periodIndex = Math.floor(diffWeeks / 2);

    this.periodStart = this.addDays(this.payPeriodAnchor, periodIndex * 14);
    this.periodEnd = this.addDays(this.periodStart, 13);
    this.refreshPeriodLabel();
  }

  private refreshPeriodLabel(): void {
    const startMonth = this.periodStart.toLocaleDateString([], {
      month: 'short',
    });
    const startDay = this.periodStart.getDate();
    const endMonth = this.periodEnd.toLocaleDateString([], { month: 'short' });
    const endDay = this.periodEnd.getDate();
    const year = this.periodEnd.getFullYear();

    this.periodLabel = `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
  }

  private sumDayTotals(groups: PeriodDayGroup[]): number {
    return groups.reduce((sum, group) => sum + group.dayTotal, 0);
  }

  private resolveEmployeeDisplayName(employee: any): string {
    if (employee.Name) {
      return String(employee.Name);
    }

    const firstName = employee.firstName ? String(employee.firstName) : '';
    const lastName = employee.lastName ? String(employee.lastName) : '';
    const fullName = `${firstName} ${lastName}`.trim();

    return fullName || 'Unknown Employee';
  }

  private getSunday(date: Date): Date {
    const sunday = new Date(date);
    sunday.setHours(0, 0, 0, 0);
    sunday.setDate(sunday.getDate() - sunday.getDay());
    return sunday;
  }

  private addDays(date: Date, days: number): Date {
    const value = new Date(date);
    value.setDate(value.getDate() + days);
    return value;
  }

  private diffInDays(start: Date, end: Date): number {
    const msPerDay = 1000 * 60 * 60 * 24;
    return Math.floor((end.getTime() - start.getTime()) / msPerDay);
  }

  private toDate(value: any): Date {
    if (value?.toDate) {
      return value.toDate();
    }
    return value instanceof Date ? value : new Date(value);
  }

  private buildGapNotes(entries: TimeEntry[]): GapNote[] {
    const notes: GapNote[] = [];

    for (let i = 0; i < entries.length - 1; i++) {
      const current = entries[i];
      const next = entries[i + 1];

      if (!current.clockOut) {
        continue;
      }

      const currentOut = this.toDate(current.clockOut);
      const nextIn = this.toDate(next.clockIn);
      const gapMinutes = Math.round(
        (nextIn.getTime() - currentOut.getTime()) / (1000 * 60),
      );

      if (gapMinutes > 0 && gapMinutes <= 15) {
        notes.push({
          afterIndex: i,
          minutes: gapMinutes,
          isVoided: true,
          label: `(${gapMinutes} min break - not deducted)`,
        });
      } else if (gapMinutes > 15) {
        notes.push({
          afterIndex: i,
          minutes: gapMinutes,
          isVoided: false,
          label: `(${gapMinutes} min lunch break)`,
        });
      }
    }

    return notes;
  }

  private toDateKey(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private toTimeInputValue(date: Date): string {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  private buildDateTime(dateKey: string, timeValue: string): Date | null {
    if (!dateKey || !timeValue) {
      return null;
    }

    const [hoursRaw, minutesRaw] = timeValue.split(':');
    const hours = Number(hoursRaw);
    const minutes = Number(minutesRaw);

    if (
      Number.isNaN(hours) ||
      Number.isNaN(minutes) ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59
    ) {
      return null;
    }

    const [yearRaw, monthRaw, dayRaw] = dateKey.split('-');
    const year = Number(yearRaw);
    const month = Number(monthRaw);
    const day = Number(dayRaw);

    if (
      Number.isNaN(year) ||
      Number.isNaN(month) ||
      Number.isNaN(day) ||
      month < 1 ||
      month > 12 ||
      day < 1 ||
      day > 31
    ) {
      return null;
    }

    return new Date(year, month - 1, day, hours, minutes, 0, 0);
  }
}
